import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Contact,
  Fingerprint,
  Hash,
  Smartphone,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { ROUTES } from '@/constants/routes';
import {
  NIN_SLIP_DESCRIPTIONS,
  NIN_SLIP_FEATURES,
  NIN_SLIP_LABELS,
  NIN_SLIP_SAMPLE_IMAGES,
  SLIP_ROUTE_MAP,
  type NinSlipTypeId,
} from '@/constants/ninSlipFeatures';
import { verificationService, getApiErrorMessage } from '@/services';
import type { NinVerificationMethod, VerificationPricingRow } from '@/types/verification';
import { cn } from '@/utils/cn';
import {
  NIN_METHOD_LABELS,
  NIN_METHOD_SHORT,
} from '@/utils/verification/ninVerificationInput';
import { notify } from '@/utils/notify';

const METHOD_OPTIONS: { id: NinVerificationMethod; icon: typeof Fingerprint }[] = [
  { id: 'by_nin_id', icon: Fingerprint },
  { id: 'by_phone', icon: Smartphone },
  { id: 'by_tracking_id', icon: Hash },
  { id: 'by_demography', icon: Contact },
];

const SLIP_TYPES: NinSlipTypeId[] = [
  'regular_slip',
  'standard_slip',
  'premium_slip',
  'advanced_slip',
];

function formatNaira(amount: number) {
  return `₦${amount.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function NinVerificationPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [pricing, setPricing] = useState<VerificationPricingRow[]>([]);
  const [ninMethod, setNinMethod] = useState<NinVerificationMethod>('by_nin_id');

  const loadPricing = useCallback(async () => {
    setLoading(true);
    try {
      const res = await verificationService.getPricing();
      setConfigured(res.data?.configured ?? true);
      setPricing(res.data?.pricing.nin ?? []);
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load verification pricing'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPricing();
  }, [loadPricing]);

  const getPlanPrice = useMemo(
    () => (slipType: NinSlipTypeId) =>
      pricing.find((p) => p.slipType === slipType && p.verificationMethod === ninMethod)?.price,
    [pricing, ninMethod],
  );

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader label="Loading verification options..." />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-start gap-3">
        <Link
          to={ROUTES.dashboardServices}
          className="mt-1 inline-flex rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-brand-navy">NIN Verification</h1>
          <p className="text-sm text-brand-muted">
            Verify by NIN, phone, tracking ID, or demography — choose a slip type and pay from your wallet.
          </p>
        </div>
      </div>

      {!configured && (
        <Card className="border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Identity verification is temporarily unavailable. Please try again later.
        </Card>
      )}

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-bold text-brand-navy">Step 1 · How do you want to verify?</h2>
          <p className="text-sm text-brand-muted">Prices update based on your selection.</p>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {METHOD_OPTIONS.map(({ id, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setNinMethod(id)}
              className={cn(
                'rounded-2xl border p-4 text-left transition',
                ninMethod === id
                  ? 'border-brand-green bg-emerald-50 shadow-sm ring-2 ring-brand-green/30'
                  : 'border-gray-200 bg-white hover:border-brand-green/40',
              )}
            >
              <Icon className="mb-3 h-5 w-5 text-brand-green" />
              <p className="font-semibold text-brand-navy">{NIN_METHOD_SHORT[id]}</p>
              <p className="mt-1 text-xs text-gray-500">{NIN_METHOD_LABELS[id]}</p>
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-bold text-brand-navy">Step 2 · Choose slip type</h2>
          <p className="text-sm text-brand-muted">Records are verified against NIMC.</p>
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {SLIP_TYPES.map((slipType) => {
            const price = getPlanPrice(slipType);
            return (
              <Card key={slipType} className="overflow-hidden">
                <div className="grid gap-4 p-5 md:grid-cols-[140px_1fr]">
                  <div className="flex min-h-[140px] items-center justify-center overflow-hidden rounded-xl border border-gray-100 bg-gray-50 p-1">
                    <img
                      src={NIN_SLIP_SAMPLE_IMAGES[slipType]}
                      alt={`${NIN_SLIP_LABELS[slipType]} preview`}
                      className="max-h-[160px] w-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-lg font-bold text-brand-navy">{NIN_SLIP_LABELS[slipType]}</h3>
                      <p className="text-sm text-gray-600">{NIN_SLIP_DESCRIPTIONS[slipType]}</p>
                    </div>
                    <ul className="space-y-1">
                      {NIN_SLIP_FEATURES[slipType].slice(0, 4).map((feature) => (
                        <li key={feature} className="flex items-center gap-2 text-xs text-gray-600">
                          <span className="h-1.5 w-1.5 rounded-full bg-brand-green" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center justify-between gap-3 pt-1">
                      <p className="text-lg font-bold text-brand-green">
                        {price != null ? formatNaira(price) : '—'}
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        disabled={!configured || price == null}
                        onClick={() =>
                          navigate(`${ROUTES.dashboardVerifyNin}/${SLIP_ROUTE_MAP[slipType]}`, {
                            state: { verificationMethod: ninMethod },
                          })
                        }
                      >
                        Continue
                        <ArrowRight className="ml-1 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </section>
    </div>
  );
}
